import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, API_URL } from "../../app/api";
import type { ComputerStatus, Me, SetupStep } from "../../app/me";

/**
 * A link to the computer's live desktop (slice 6c §6): a bearer credential.
 * It lives in the sheet's state and its iframe only: never in the query
 * cache, storage, a log or Sentry (`src/sentry-scrub.ts`).
 */
export type DesktopLink = { id: string; url: string; expires_at: string; issued_at: string };

/** A fresh link to watch with. `409 asleep`: watching never wakes the computer. */
export const watchLink = (signal?: AbortSignal) =>
  api<DesktopLink>("POST", "/api/computer/desktop", { mode: "watch" }, { signal });

/**
 * Who opened the computer's screen last (the `desktop` hub event): Boat's
 * stream has one viewer, so a sheet whose link isn't this one has lost it.
 * Never the link. Kept in the query cache.
 */
export type Viewer = { link_id: string; name: string; issued_at: string };

const viewerKey = ["desktop", "viewer"] as const;

/** The later-issued of two viewers: an answer or event that comes late never undoes a newer one. */
const newer = (a: Viewer | null | undefined, b: Viewer | null): Viewer | null =>
  !a ? b : !b || a.issued_at >= b.issued_at ? a : b;

export const setViewer = (client: QueryClient, viewer: Viewer) =>
  client.setQueryData<Viewer | null>(viewerKey, (now) => newer(now, viewer));

/**
 * The `desktop` events keep it current; a reconnect refetches it, since the
 * hub has no replay and an event missed meanwhile is gone (Devin on #373).
 */
export function useViewer(): Viewer | null {
  const client = useQueryClient();
  return (
    useQuery<Viewer | null>({
      queryKey: viewerKey,
      // An event that came while the read was out wins over an older answer (Codex on #373).
      queryFn: async () => {
        const read = await api<{ viewer: Viewer | null }>("GET", "/api/computer/desktop/viewer");
        // The cache as it is once the answer's in (Codex on #373).
        return newer(client.getQueryData<Viewer | null>(viewerKey), read.viewer);
      },
      staleTime: Infinity,
    }).data ?? null
  );
}

/** A person as a handoff names them. */
type Who = { id: string; name: string };

/** A handoff as `GET /api/threads/:id` lists it (slice 6c §3.2): its card renders from it. */
export type Handoff = {
  id: string;
  thread_id: string;
  turn_id: string | null;
  agent_id: string;
  /** What the person does, as a title: "Walmart wants a human check". */
  reason: string;
  url: string | null;
  /** The page's host, `www.` dropped ("walmart.ca"). */
  host: string | null;
  /** The screen still the agent took with its ask (`GET /api/files/:id`). */
  still_file_id: string | null;
  status: "waiting" | "taken" | "done" | "skipped" | "expired";
  for: Who | null;
  taken_by: Who | null;
  taken_at: string | null;
  ended_by: Who | null;
  ended_at: string | null;
  /** `timeout`: the clock ended it (30 minutes taken, or an hour waiting). */
  ended_how: "person" | "timeout" | null;
  /** The agent's turn its ending started; null on an expired one until Ask again. */
  next_turn_id: string | null;
  created_at: string;
};

/**
 * Take over (§3.3), or Continue for its taker: the computer held, and a
 * control link, or why there's none (`{ error }`: the take stands; ask
 * again with `controlLink`).
 */
export const takeHandoff = (id: string) =>
  api<{ handoff: Handoff; desktop: DesktopLink | { error: string } }>(
    "POST",
    `/api/handoffs/${id}/take`,
  );

/** I'm done (§3.4), with an optional note (≤ 500): the agent's next turn starts. */
export const handoffDone = (id: string, note: string | null) =>
  api<{ handoff: Handoff }>("POST", `/api/handoffs/${id}/done`, note ? { note } : {});

/** Skip (§3.4), from waiting or taken: the agent carries on without it. */
export const handoffSkip = (id: string) =>
  api<{ handoff: Handoff }>("POST", `/api/handoffs/${id}/skip`);

/** Ask again (§3.4), once, on one nobody took. */
export const handoffAgain = (id: string) =>
  api<{ handoff: Handoff }>("POST", `/api/handoffs/${id}/again`);

/** A new control link for the person holding the computer: never to keep a hold (§5). */
export const controlLink = (signal?: AbortSignal) =>
  api<DesktopLink>("POST", "/api/computer/desktop", { mode: "control" }, { signal });

/** Keeps the caller's hold 15 more minutes (§5); the sheet calls it every 5 while open. */
export const keepHold = () => api<null>("POST", "/api/computer/desktop/keep");

/** What the Computer page shows (slice 6c §5), from `GET /api/computer/desktop/state`. */
export type ComputerState = {
  status: ComputerStatus;
  step: SetupStep | null;
  desktop: boolean;
  /** S-455: Take over works on a phone; false for a pi computer's lean box. Missing on an older Worker. */
  phone_control?: boolean;
  /** Spike S-429: chats are answered without a computer (a pi workspace). Missing on an older Worker. */
  no_computer?: boolean;
  asleep_at: string | null;
  /** Who holds it, until when; a handoff's hold names its chat. */
  held: { user_id: string; name: string; until: string; handoff_thread_id: string | null } | null;
  /** The turn the computer is on: claimed or running. */
  working: { turn_id: string; thread_id: string; title: string | null; agent: string } | null;
  /** Asks queued and not yet claimed. */
  waiting: number;
};

/** Refetched on the `computer`, `turn`, `handoff` and `desktop` events (`events.ts`). */
export const computerStateKey = ["computer", "state"] as const;

export function useComputerState({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: computerStateKey,
    enabled,
    queryFn: async () =>
      (await api<{ computer: ComputerState }>("GET", "/api/computer/desktop/state")).computer,
  });
}

/**
 * Take over from the Computer page (§5): the computer held for the caller,
 * then a control link; an asleep one wakes first (`{ waking: true }`), and
 * once it's ready `controlLink` gives the link.
 */
export const takeOver = () =>
  api<DesktopLink | { waking: true }>("POST", "/api/computer/desktop/take");

/** Done, or Cancel while it wakes (§5): the caller's hold let go. */
export const releaseHold = () => api<null>("POST", "/api/computer/desktop/release");

/** Leaving the page (§5): the release sent as the page goes, so it outlives it. */
export const releaseOnLeave = () =>
  void fetch(`${API_URL}/api/computer/desktop/release`, {
    method: "POST",
    credentials: "include",
    keepalive: true,
  }).catch(() => undefined);

/**
 * Who holds the computer, as a queued turn's line names them (§5: "Waiting:
 * Nadia is using the computer"): `/api/me` says who, the page's state their name.
 */
export function useHolder(
  me: Pick<Me, "user" | "computer">,
): { name: string; you: boolean } | null {
  const heldBy = me.computer?.held_by ?? null;
  const you = heldBy === me.user.id;
  const state = useComputerState({ enabled: heldBy !== null && !you });
  if (!heldBy) return null;
  return { name: you ? "you" : (state.data?.held?.name ?? "Someone"), you };
}
